import { useState, useEffect } from "react";
import BaseModal from "../../BaseComponents/BaseModal/BaseModal";
import SelectBox from "../../BaseComponents/SelectBox/SelectBox";
import styles from "./AddEditPaymentMethodModal.module.css";

const emptyForm = {
  name: "",
  type: "",
  description: "",
  status: "Active",
};

const typeOptions = [
  { label: "Select type", value: "" },
  { label: "Bank", value: "Bank" },
  { label: "Card", value: "Card" },
  { label: "Cash", value: "Cash" },
];

const statusOptions = [
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" },
];

const AddEditPaymentMethodModal = ({
  open,
  onClose,
  mode = "add",
  initialData,
  onSave,
  saveLoading = false,
}) => {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialData) {
      setForm({
        name: initialData.name || "",
        type: initialData.type || "",
        description: initialData.description || "",
        status: initialData.status || "Active",
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, mode, initialData]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (!form.name.trim()) return;
    if (!form.type) return;

    const payload = {
      ...form,
      name: form.name.trim(),
      description: form.description.trim(),
    };

    onSave(payload);
  };

  const title = mode === "edit" ? "Edit Payment Method" : "Add Payment Method";

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={title}
      width="560px"
      footer={
        <>
          <button className={styles.cancelBtn} onClick={onClose} disabled={saveLoading}>
            Cancel
          </button>
          <button
            className={`${styles.primaryBtn} ${saveLoading ? styles.primaryBtnLoading : ""}`}
            onClick={handleSubmit}
            disabled={saveLoading}
          >
            {saveLoading && <span className={styles.spinner} />}
            {mode === "edit" ? "Update Method" : "Add Method"}
          </button>
        </>
      }
    >
      <div className={styles.formGrid}>
        <div className={`${styles.field} ${styles.fullWidth}`}>
          <label>Method Name *</label>
          <input
            type="text"
            placeholder="e.g., Bank Transfer, Visa, Cash"
            value={form.name}
            onChange={(e) => handleChange("name", e.target.value)}
            autoFocus
          />
        </div>

        <div className={styles.field}>
          <label>Type *</label>
          <SelectBox
            value={form.type}
            options={typeOptions}
            onChange={(e) => handleChange("type", e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label>Status *</label>
          <SelectBox
            value={form.status}
            options={statusOptions}
            onChange={(e) => handleChange("status", e.target.value)}
          />
        </div>

        <div className={`${styles.field} ${styles.fullWidth}`}>
          <label>Description</label>
          <textarea
            placeholder="Brief description of this payment method..."
            value={form.description}
            onChange={(e) => handleChange("description", e.target.value)}
            rows={3}
          />
        </div>
      </div>

      {mode === "edit" && initialData?.usageCount > 0 && (
        <div className={styles.warningBox}>
          <span className={styles.warningIcon}>⚠️</span>
          <div>
            <div className={styles.warningTitle}>Method in use</div>
            <div className={styles.warningText}>
              This payment method has been used in {initialData.usageCount.toLocaleString()} transactions.
              Deactivating it will not affect existing records, but it will prevent future use.
            </div>
          </div>
        </div>
      )}
    </BaseModal>
  );
};

export default AddEditPaymentMethodModal;